"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Place = exports.placeComposedAttributes = exports.placeAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const geojson_validation_1 = require("geojson-validation");
const Address_1 = require("./Address");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.placeAttributes = [
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    'geography',
    'name',
    'shortname',
    'osmId',
    'propertyRegistryId',
    'buildingId',
    'internalId',
    'parkingType',
    'parkingFeeType',
    'geocodingPrecisionCategory',
    'geocodingPrecisionMeters',
    'geocodingQueryString',
    'geocodingName',
    'lastAction',
    'deviceUsed',
    'zoom',
    'preData',
    'preGeography'
];
exports.placeComposedAttributes = [...exports.placeAttributes, '_address'];
/**
 * A place is a location (GeoJSON point) with attributes.
 * Classes can inherit this class and add their own attributes (like a work place, a school place, a junction, etc.).
 */
class Place extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, ['_address', 'address']), exports.placeAttributes, exports.placeComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.address = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._address, Address_1.Address.unserialize, this._surveyObjectsRegistry);
        this._surveyObjectsRegistry.registerPlace(this);
    }
    /**
     * Check if the geography attribute is valid and if it is a point feature
     * @returns {Optional<boolean>} - Returns true if the geography attribute is valid, false if not, or undefined if no geography
     */
    geographyIsValid() {
        return this.geography
            ? (0, geojson_validation_1.isFeature)(this.geography) && this.geography.geometry && (0, geojson_validation_1.isPoint)(this.geography.geometry)
            : undefined;
    }
    /**
     * Check if the preGeography attribute is valid and if it is a point feature
     * @returns {Optional<boolean>} - Returns true if the preGeography attribute is valid, false if not, or undefined if no preGeography
     */
    preGeographyIsValid() {
        return this.preGeography
            ? (0, geojson_validation_1.isFeature)(this.preGeography) && this.preGeography.geometry && (0, geojson_validation_1.isPoint)(this.preGeography.geometry)
            : undefined;
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get address() {
        return this._address;
    }
    set address(value) {
        this._address = value;
    }
    get name() {
        return this._attributes.name;
    }
    set name(value) {
        this._attributes.name = value;
    }
    get shortname() {
        return this._attributes.shortname;
    }
    set shortname(value) {
        this._attributes.shortname = value;
    }
    get osmId() {
        return this._attributes.osmId;
    }
    set osmId(value) {
        this._attributes.osmId = value;
    }
    get propertyRegistryId() {
        return this._attributes.propertyRegistryId;
    }
    set propertyRegistryId(value) {
        this._attributes.propertyRegistryId = value;
    }
    get buildingId() {
        return this._attributes.buildingId;
    }
    set buildingId(value) {
        this._attributes.buildingId = value;
    }
    get internalId() {
        return this._attributes.internalId;
    }
    set internalId(value) {
        this._attributes.internalId = value;
    }
    get parkingType() {
        return this._attributes.parkingType;
    }
    set parkingType(value) {
        this._attributes.parkingType = value;
    }
    get parkingFeeType() {
        return this._attributes.parkingFeeType;
    }
    set parkingFeeType(value) {
        this._attributes.parkingFeeType = value;
    }
    get geocodingPrecisionCategory() {
        return this._attributes.geocodingPrecisionCategory;
    }
    set geocodingPrecisionCategory(value) {
        this._attributes.geocodingPrecisionCategory = value;
    }
    get geocodingPrecisionMeters() {
        return this._attributes.geocodingPrecisionMeters;
    }
    set geocodingPrecisionMeters(value) {
        this._attributes.geocodingPrecisionMeters = value;
    }
    get geocodingQueryString() {
        return this._attributes.geocodingQueryString;
    }
    set geocodingQueryString(value) {
        this._attributes.geocodingQueryString = value;
    }
    get geocodingName() {
        return this._attributes.geocodingName;
    }
    set geocodingName(value) {
        this._attributes.geocodingName = value;
    }
    get lastAction() {
        return this._attributes.lastAction;
    }
    set lastAction(value) {
        this._attributes.lastAction = value;
    }
    get deviceUsed() {
        return this._attributes.deviceUsed;
    }
    set deviceUsed(value) {
        this._attributes.deviceUsed = value;
    }
    get zoom() {
        return this._attributes.zoom;
    }
    set zoom(value) {
        this._attributes.zoom = value;
    }
    get geography() {
        return this._attributes.geography;
    }
    set geography(value) {
        this._attributes.geography = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get preGeography() {
        return this._attributes.preGeography;
    }
    set preGeography(value) {
        this._attributes.preGeography = value;
    }
    /**
     * Creates a Place object from sanitized parameters
     * @param {ExtendedPlaceAttributes | SerializedExtendedPlaceAttributes} params - Sanitized place parameters
     * @returns {Place} New Place instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Place(flattenedParams, surveyObjectsRegistry);
    }
    /**
     * Factory that validates input from an interview and makes
     * sure types and required fields are valid before returning a new object
     * Custom attributes must be validated in each survey project
     * @param dirtyParams
     * @returns Place | Error[]
     */
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Place.validateParams(dirtyParams);
        const place = errors.length === 0 ? new Place(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(place);
    }
    /**
     * Validates attributes types
     * @param dirtyParams The params input
     * @param displayName The name of the object to validate, for error display
     * @returns Error[]
     */
    static validateParams(dirtyParams, displayName = 'Place') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid:
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate _isValid:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        // Validate _weights:
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isGeojsonPoint('geography', dirtyParams.geography, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('name', dirtyParams.name, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('shortname', dirtyParams.shortname, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('osmId', dirtyParams.osmId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('propertyRegistryId', dirtyParams.propertyRegistryId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('buildingId', dirtyParams.buildingId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('internalId', dirtyParams.internalId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('parkingType', dirtyParams.parkingType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('parkingFeeType', dirtyParams.parkingFeeType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('geocodingPrecisionCategory', dirtyParams.geocodingPrecisionCategory, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveNumber('geocodingPrecisionMeters', dirtyParams.geocodingPrecisionMeters, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('geocodingQueryString', dirtyParams.geocodingQueryString, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('geocodingName', dirtyParams.geocodingName, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('lastAction', dirtyParams.lastAction, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('deviceUsed', dirtyParams.deviceUsed, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('zoom', dirtyParams.zoom, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isGeojsonPoint('preGeography', dirtyParams.preGeography, displayName));
        const addressAttributes = dirtyParams._address;
        if (addressAttributes) {
            errors.push(...Address_1.Address.validateParams(addressAttributes, 'Address'));
        }
        return errors;
    }
}
exports.Place = Place;
Place._confidentialAttributes = ['preData'];
//# sourceMappingURL=Place.js.map